import * as THREE from "three";
import type { WyrmDef } from "../rare/wyrmApi";
import type { SceneRig } from "../render/scene";
import { PostFx } from "../render/postfx";
import { groundAt } from "../render/terrain";
import { ScreenFlash } from "../ui/screenFlash";
import type { LevelScene } from "./levelScene";

/** 0xRRGGBB → the sprite VFX's [r, g, b] (0–1). */
export const rgb = (hex: number): [number, number, number] => [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
const hexCss = (hex: number): string => `#${hex.toString(16).padStart(6, "0")}`;
const _up = new THREE.Vector3();
const _ground = new THREE.Vector3();

/**
 * The Starfall Wyrm's beats in the world (M40), for level/wyrmMoment.ts: the dive's slam, the kill's burst and the
 * flee's flash — the sprite VFX (art/vfx.js: flash, sparkles, ring, ascend burst, light), the DOM screen flash, the
 * exposure punch, camera trauma and the trees bowing. Every beat reads where the Wyrm is drawn (EnemyViews.rareDrawn)
 * and does nothing it cannot (no VFX atlas yet, no body on screen). Reduced effects: no trauma, a tenth of the sprites.
 */
export function wyrmSlam(scene: LevelScene, rig: SceneRig, def: WyrmDef, reduced: boolean, big: boolean): void {
  const fx = scene.router?.vfx, drawn = scene.enemies.rareDrawn();
  if (fx && drawn) {
    _up.copy(drawn.centre);
    _ground.set(drawn.feet.x, groundAt(drawn.feet.x, drawn.feet.z) + 0.05, drawn.feet.z);
    const n = reduced ? 8 : big ? 60 : 36;
    fx.flash(_up, big ? 1.8 : 1.4, rgb(0xfff4e8));            // a kick of light, never a white-out over the model
    fx.sparkles(_up, n, { color: rgb(def.sparkA), radius: 1.4, rise: 2.0, life: 1.6, size: 0.17 });
    if (!reduced) {
      fx.sparkles(_up, n * 0.6, { color: rgb(def.sparkB), radius: 1.1, rise: 2.6, life: 1.8, size: 0.13 });
      fx.sparkles(_up, n * 0.6, { color: rgb(def.sparkC), radius: 1.8, rise: 1.2, life: 1.4, size: 0.12 });
      fx.ring(_ground, { size: big ? 4.2 : 3.0, life: 0.7, color: rgb(def.sparkA), flat: true, bright: 3 });
      fx.ring(_ground, { size: big ? 2.6 : 1.8, life: 0.5, color: rgb(def.sparkC), flat: true, bright: 2.6 });
    }
    fx.light(_up, rgb(def.sparkA), 6, 4, 2.6);
  }
  ScreenFlash.flash("#fff6e6", big ? 0.45 : 0.35, 420);
  PostFx.exposurePunch(big ? 1.3 : 1.2, 560);
  if (!reduced) rig.iso.addTrauma(big ? 0.62 : 0.45);
  if (drawn) scene.grid.foliage.gustAround({ x: drawn.feet.x, y: drawn.feet.z }, big ? 5 : 3.5);
}

/** The roar's kick: a lilac flash at its throat, a light, a shake. */
export function wyrmRoarFx(scene: LevelScene, rig: SceneRig, def: WyrmDef, reduced: boolean): void {
  const fx = scene.router?.vfx, drawn = scene.enemies.rareDrawn();
  if (fx && drawn) {
    fx.flash(drawn.centre, 1.0, rgb(def.sparkA));
    if (!reduced) fx.sparkles(drawn.centre, 18, { color: rgb(def.sparkC), radius: 0.9, rise: 1.4, life: 1.0, size: 0.12 });
  }
  if (!reduced) rig.iso.addTrauma(0.32);
}

/**
 * The kill (where it fell, `centre` in the air and `feet` on the ground): the tier-up's shockwave and spiral fountain in
 * lilac, the ascend burst in rose-gold, a storm of stars and confetti in all three colours, a white-hot flash, a light
 * that lingers, a prismatic screen flash and the trees bowing.
 */
export function wyrmKillBurst(scene: LevelScene, rig: SceneRig, def: WyrmDef, centre: THREE.Vector3 | null, feet: THREE.Vector3 | null, height: number, reduced: boolean): void {
  const fx = scene.router?.vfx;
  if (fx && centre && feet) {
    _up.copy(centre);
    _ground.set(feet.x, groundAt(feet.x, feet.z) + 0.05, feet.z);
    fx.towerLevelUp(_ground, Math.max(1, height * 2), { color: rgb(def.sparkA) });
    fx.ascendBurst(_up, Math.max(1, height * 1.6), rgb(def.sparkB), 3);
    fx.sparkles(_up, reduced ? 16 : 90, { color: rgb(def.sparkA), radius: 1.3, rise: 1.8, life: 1.8, size: 0.18 });
    if (!reduced) {
      fx.sparkles(_up, 60, { color: rgb(def.sparkB), radius: 0.9, rise: 2.6, life: 2.0, size: 0.14 });
      fx.sparkles(_up, 60, { color: rgb(def.sparkC), radius: 1.6, rise: 2.2, life: 1.9, size: 0.13 });
      fx.sparkles(_up, 40, { color: [1, 1, 1], radius: 0.6, rise: 3.0, life: 1.4, size: 0.1 });
      fx.ring(_ground, { size: 4.8, life: 0.9, color: rgb(def.sparkB), flat: true, bright: 3.2 });
    }
    fx.flash(_up, 1.9, [1, 0.97, 0.92]);                     // small: the falling beast stays in view
    fx.light(_up, rgb(def.sparkA), 7, 4, 2.2);
  }
  ScreenFlash.flash(hexCss(0xfff3ff), 0.45, 520);
  PostFx.exposurePunch(1.35, 700);
  if (!reduced) rig.iso.addTrauma(0.7);
  if (feet) scene.grid.foliage.gustAround({ x: feet.x, y: feet.z }, 5);
}

/** It gets away: a cold violet flash, a sweep of stars where it rose, a shake. */
export function wyrmFleeFx(scene: LevelScene, rig: SceneRig, def: WyrmDef, reduced: boolean): void {
  const fx = scene.router?.vfx, drawn = scene.enemies.rareDrawn();
  if (fx && drawn && !reduced) fx.sparkles(drawn.centre, 40, { color: rgb(def.sparkC), radius: 1.2, rise: 3.2, life: 1.6, size: 0.14 });
  ScreenFlash.flash(hexCss(0x5a3cff), 0.4, 600);
  if (!reduced) rig.iso.addTrauma(0.35);
}
